import React, { useState, useMemo, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Layout, Menu, Avatar, Badge, Tooltip, theme as antdTheme, Grid, Drawer, Button, Typography } from 'antd';
import type { MenuProps } from 'antd';
import { useAppContext } from '../context/AppContext';
import { useTheme } from '../context/ThemeContext';
import { useReportsAccess } from '../context/ReportsAccessContext';
import { useAuth } from '../context/AuthContext';
import {
  UserOutlined,
  MenuOutlined,
  LogoutOutlined,
  ArrowLeftOutlined,
  DashboardOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
  AuditOutlined,
  SafetyOutlined,
  FolderOpenOutlined,
} from '@ant-design/icons';


const { Sider } = Layout;
const { useBreakpoint } = Grid;

interface SidebarProps {
  collapsible: boolean;
  isMobile?: boolean;
  isSmallDesktop?: boolean;
}

const ReportsNavigation = React.memo(() => {
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const isSmallDesktop = screens.md && !screens.lg;
  return <Sidebar collapsible={!isMobile} isMobile={isMobile} isSmallDesktop={isSmallDesktop} />;
});

const Sidebar = React.memo(({ collapsible, isMobile, isSmallDesktop }: SidebarProps) => {
  const location = useLocation();
  const { state: stateApp, appContextAction } = useAppContext();
  const { isNavMenuClose } = stateApp;
  const { navMenu } = appContextAction;
  const { theme } = useTheme();
  const { user, logout } = useAuth();
  const isDarkMode = theme.algorithm?.[0] === antdTheme.darkAlgorithm;
  const { access } = useReportsAccess();

  const [drawerVisible, setDrawerVisible] = useState(false);

  const getThemeStyles = useMemo(
    () => ({
      sidebarHeader: {
        padding: isNavMenuClose && !isMobile ? '8px' : '0 16px',
        display: 'flex',
        flexDirection: isNavMenuClose && !isMobile ? 'column' as const : 'row' as const,
        alignItems: 'center',
        justifyContent: isNavMenuClose && !isMobile ? 'center' : 'space-between',
        overflow: 'hidden',
        transition: 'all 0.3s ease',
        borderBottom: 'none',
        backgroundColor: 'transparent',
      },
      logoContainer: {
        display: 'flex',
        alignItems: 'center',
        paddingBottom: isNavMenuClose && !isMobile ? '8px' : '0',
        justifyContent: isNavMenuClose && !isMobile ? 'center' : 'flex-start',
        marginBottom: isNavMenuClose && !isMobile ? '8px' : '0',
      },
      logo: {
        width: isNavMenuClose && !isMobile ? '50px' : isSmallDesktop ? '180px' : '200px',
        height: 'auto',
        transition: 'width 0.3s ease',
      },
      toggleButton: {
        color: isDarkMode ? '#fff' : '#000',
        background: 'transparent',
        marginTop: isNavMenuClose && !isMobile ? '12px' : undefined,
      },
      userEmail: {
        fontSize: '12px',
        opacity: 0.85,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        color: isDarkMode ? 'rgba(255,255,255,0.65)' : 'rgba(0,0,0,0.65)',
      },
      collapsedProfile: {
        padding: '16px 0',
        display: 'flex',
        justifyContent: 'center',
        borderBottom: 'none',
        marginBottom: '8px',
        backgroundColor: 'transparent',
      },
      navigationMenu: {
        flex: 1,
        overflowY: 'auto' as const,
        overflowX: 'hidden' as const,
        padding: '0',
        height: 'auto',
        minHeight: 0,
      },
      sidebarStyle: {
        boxShadow: 'none',
        zIndex: 1000,
        height: '100vh',
        position: 'fixed' as const,
        left: 0,
        display: 'flex',
        flexDirection: 'column' as const,
        transition: 'all 0.2s ease',
        background: 'transparent',
        backgroundColor: 'transparent',
        overflow: 'hidden',
        ...(isSmallDesktop &&
          !isNavMenuClose && {
            width: '220px !important',
          }),
      },
    }),
    [isDarkMode, isNavMenuClose, isMobile, isSmallDesktop]
  );

  const currentPath = useMemo(() => {
    const path = location.pathname;
    const fromState = (location.state as { from?: string })?.from;
    
    if (path === '/reports/dashboard') return 'dashboard';
    if (path === '/reports/payment-reports') return 'payment-reports';
    if (path === '/reports/payment-reports/new') return 'my-reports';
    if (path === '/reports/my-reports') return 'my-reports';
    if (path === '/reports/pending-review') return 'pending-review';
    if (path === '/reports/pending-review-2') return 'pending-review-2';
    if (path === '/reports/pending-recommendation') return 'pending-recommendation';
    if (path === '/reports/approved-reports') return 'approved-reports';
    if (path === '/reports/pending-sanction') return 'pending-sanction';
    if (path === '/reports/pending-sanction-level-2') return 'pending-sanction-level-2';

    if (path.startsWith('/reports/payment-reports/')) {
      if (fromState?.startsWith('/reports/payment-reports')) return 'payment-reports';
      if (fromState?.startsWith('/reports/my-reports')) return 'my-reports';
      if (fromState?.startsWith('/reports/approved-reports')) return 'approved-reports';
      return 'my-reports';
    }

    const segments = path.split('/').filter(Boolean);
    return segments.length > 0 ? segments[segments.length - 1] : '';
  }, [location.pathname, location.state]);

  const menuItems: MenuProps['items'] = useMemo(() => {
    const items: MenuProps['items'] = [];

    items.push({
      key: 'dashboard',
      icon: <DashboardOutlined />,
      label: <Link to="/reports/dashboard">Dashboard</Link>,
    });

    if (user?.isadmin || access?.canViewAllReports) {
      items.push({
        key: 'payment-reports',
        icon: <FileTextOutlined />,
        label: <Link to="/reports/payment-reports">All Payment Reports</Link>,
      });
    }

    items.push({
      key: 'my-reports',
      icon: <FolderOpenOutlined />,
      label: <Link to="/reports/my-reports">My Reports</Link>,
    });

    if (access?.isReviewer) {
      items.push({
        key: 'pending-review',
        icon: <CheckCircleOutlined />,
        label: <Link to="/reports/pending-review">Pending Review</Link>,
      });
    }

    if (access?.isReviewer2) {
      items.push({
        key: 'pending-review-2',
        icon: <CheckCircleOutlined />,
        label: <Link to="/reports/pending-review-2">Pending Review (Level 2)</Link>,
      });
    }

    if (access?.isRecommender) {
      items.push({
        key: 'pending-recommendation',
        icon: <AuditOutlined />,
        label: <Link to="/reports/pending-recommendation">Pending Recommendation</Link>,
      });
      items.push({
        key: 'approved-reports',
        icon: <CheckCircleOutlined />,
        label: <Link to="/reports/approved-reports">Approved Reports</Link>,
      });
    }

    if (access?.isSanctioner1 || access?.isSanctioner2) {
      items.push({
        key: 'pending-sanction',
        icon: <SafetyOutlined />,
        label: <Link to="/reports/pending-sanction">Pending Sanction (Level 1)</Link>,
      });
    }

    if (access?.isSanctioner2) {
      items.push({
        key: 'pending-sanction-level-2',
        icon: <SafetyOutlined />,
        label: <Link to="/reports/pending-sanction-level-2">Pending Sanction (Level 2)</Link>,
      });
    }

    items.push({ type: 'divider' });

    items.push({
      key: 'back-to-stages',
      icon: <ArrowLeftOutlined />,
      label: <Link to="/project-selection">Back to Previous</Link>,
    });

    items.push({
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Logout',
      onClick: logout,
      danger: true,
    });

    return items;
  }, [access, logout, user?.isadmin]);

  const toggleCollapse = useCallback(() => {
    if (isNavMenuClose) {
      navMenu.expand();
    } else {
      navMenu.collapse();
    }
  }, [isNavMenuClose, navMenu]);

  const handleMenuClick = useCallback(
    (info: any) => {
      if (info.key === 'logout') {
        logout();
      }

      if (isMobile) {
        setDrawerVisible(false);
      }
    },
    [logout, isMobile]
  );

  const toggleDrawer = useCallback(() => {
    setDrawerVisible((prev) => !prev);
  }, []);

  const SidebarContent = useMemo(
    () => (
      <div
        className="transparent-nav"
        style={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <div className="sidebar-header" style={getThemeStyles.sidebarHeader}>
          <div className="logo-container" style={getThemeStyles.logoContainer}>
            {isNavMenuClose && !isMobile ? (
              <div style={{
                width: 42,
                height: 42,
                borderRadius: '10px',
                background: 'linear-gradient(135deg, #1d8348 0%, #145a32 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                margin: '12px 0',
              }}>
                <Typography.Text style={{
                  color: '#ffffff',
                  fontWeight: 700,
                  fontSize: '16px',
                  lineHeight: 1,
                  letterSpacing: '-0.5px',
                }}>
                  CC
                </Typography.Text>
              </div>
            ) : (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '16px 0',
              }}>
                <div style={{
                  width: 40,
                  height: 40,
                  borderRadius: '10px',
                  background: 'linear-gradient(135deg, #1d8348 0%, #145a32 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}>
                  <Typography.Text style={{
                    color: '#ffffff',
                    fontWeight: 700,
                    fontSize: '16px',
                    lineHeight: 1,
                    letterSpacing: '-0.5px',
                  }}>
                    CC
                  </Typography.Text>
                </div>
                <div style={{ overflow: 'hidden' }}>
                  <Typography.Text style={{
                    display: 'block',
                    fontWeight: 700,
                    fontSize: isSmallDesktop ? '13px' : '14px',
                    color: isDarkMode ? '#ffffff' : '#1a1a1a',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    lineHeight: 1.3,
                  }}>
                    Climate Connections
                  </Typography.Text>
                  <Typography.Text style={{
                    display: 'block',
                    fontWeight: 500,
                    fontSize: '12px',
                    color: isDarkMode ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.45)',
                    whiteSpace: 'nowrap',
                    lineHeight: 1.3,
                    marginTop: '2px',
                  }}>
                    LLP
                  </Typography.Text>
                </div>
              </div>
            )}
          </div>
        </div>

        {isNavMenuClose && !isMobile && (
          <div style={getThemeStyles.collapsedProfile}>
            <Tooltip title={user?.name || 'User'} placement="right">
              <Badge dot status="success" offset={[-4, 28]}>
                <Avatar icon={<UserOutlined />} size={36} />
              </Badge>
            </Tooltip>
          </div>
        )}

        <div className="navigation-menu" style={getThemeStyles.navigationMenu}>
          <Menu
            mode="inline"
            selectedKeys={[currentPath]}
            defaultOpenKeys={isMobile ? ['main', 'settings'] : []}
            items={menuItems}
            onClick={handleMenuClick}
            style={{
              border: 'none',
              background: 'transparent',
              height: '100%',
            }}
            theme={isDarkMode ? 'dark' : 'light'}
          />
        </div>
      </div>
    ),
    [
      getThemeStyles,
      isNavMenuClose,
      isMobile,
      user,
      currentPath,
      menuItems,
      handleMenuClick,
      isDarkMode,
    ]
  );

  if (isMobile) {
    return (
      <>
        <Button
          type="primary"
          shape="circle"
          icon={<MenuOutlined />}
          onClick={toggleDrawer}
          style={{
            position: 'fixed',
            top: '16px',
            left: '16px',
            zIndex: 999,
            boxShadow: '0 3px 6px rgba(0,0,0,0.2)',
          }}
          size="middle"
        />
        <Drawer
          placement="left"
          onClose={toggleDrawer}
          open={drawerVisible}
          width={320}
          styles={{
            body: {
              padding: 0,
              height: '100%',
              background: 'transparent',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            },
          }}
          closable={false}
        >
          {SidebarContent}
        </Drawer>
      </>
    );
  }

  return (
    <Sider
      collapsible
      collapsed={isNavMenuClose}
      onCollapse={toggleCollapse}
      className="navigation-sidebar transparent-nav"
      theme={isDarkMode ? 'dark' : 'light'}
      width={isSmallDesktop ? 220 : 260}
      collapsedWidth={80}
      style={getThemeStyles.sidebarStyle}
      trigger={null}
    >
      {SidebarContent}
    </Sider>
  );
});

export default ReportsNavigation;
